import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toastConfig } from 'lib/reactToastify/config';
import { toast } from 'react-toastify';

import { queryKeys } from 'api/config';
import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';
import { Nullable } from 'shared_DEPRECATED/types';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';
import { dateUtils } from 'shared_DEPRECATED/utils';
import { LinkPreview } from 'types/common';

import { ScheduledMessagesListResponse } from 'features/chat/config/types/ScheduledMessage';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';

const VALIDATION_ERROR = 'validation_error';

type UseChatMessageScheduleCreateProps = {
  onSuccess?: (data: any) => void;
};

export const useChatMessageScheduleCreate = ({
  onSuccess,
}: UseChatMessageScheduleCreateProps) => {
  const { chatId } = useChatInterlocutorContext();
  const { request } = useMutationHTTPRequest();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      scheduledFor,
      text,
      replyTo,
      attachments,
      linkPreview,
    }: {
      scheduledFor: string;
      text: string;
      replyTo: string | null;
      attachments?: Attachment[];
      linkPreview: Nullable<LinkPreview>;
    }) =>
      request({
        url: `/api/chats/${chatId}/scheduled-messages/create`,
        body: {
          scheduledFor,
          text,
          replyTo,
          attachments,
          linkPreview,
        },
      }),
    {
      onMutate: async ({
        scheduledFor,
        text,
        replyTo,
        attachments,
        linkPreview,
      }: {
        scheduledFor: string;
        text: string;
        replyTo: string | null;
        attachments: Attachment[];
        linkPreview: Nullable<LinkPreview>;
      }) => {
        await queryClient.cancelQueries([queryKeys.scheduledMessages, chatId]);

        const prevMessages =
          queryClient.getQueryData<ScheduledMessagesListResponse>([
            queryKeys.scheduledMessages,
            chatId,
          ]);

        queryClient.setQueryData(
          [queryKeys.scheduledMessages, chatId],
          ({ messages }) => {
            return {
              messages: [
                ...messages,
                {
                  text,
                  scheduledFor,
                  attachments,
                  replyTo,
                  messageId: dateUtils().unixTimestamp(),
                  linkPreview,
                },
              ],
            };
          }
        );

        return { prevMessages };
      },
      onSuccess,
      onSettled: () => {
        queryClient.invalidateQueries([queryKeys.scheduledMessages, chatId]);
      },
      onError: (error: Error, __, context) => {
        if (error.name === VALIDATION_ERROR) {
          toast.error('Scheduled time must be at least 5 minutes from now.', {
            icon: false,
            ...toastConfig,
          });
        }
        queryClient.setQueryData(
          [queryKeys.scheduledMessages, chatId],
          context?.prevMessages
        );

        logError(error);
      },
    }
  );
};
